/* Importing the necessary libraries and files */
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';

/* Importing the components from the material-ui library. */
import {
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  IconButton,
  Link,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Edit as EditIcon, HelpOutline as HelpOutlineIcon } from '@mui/icons-material';

/* Importing components from the components folder. */
import AutoCompleteLocation from '../../components/AutoCompleteLocation';
import FormHelpDialog from '../../components/FormHelpDialog';
import Page from '../../components/Page';

/* Importing the functions from the Redux slices. */
import { getLocation } from '../../features/location/locationSlice';

/* Importing constants from LocationValueSets.js file. */
import {
  LOCATION_STATUS,
  LOCATION_OPERATIONAL_STATUS,
  LOCATION_MODE,
  LOCATION_PHYSICAL_TYPE,
} from '../../utils/Location/Location.utils.ValueSets';

/* Importing the functions from the utils folder. */
import fetchAndHandleData from '../../utils/fetchAndHandleData';
import { fLocation } from '../../utils/Location/Location.utils.format';

/**
 * Renders the Location Details page.
 * @returns {JSX.Element} LocationDetails component
 */
export default function LocationDetails() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();

  const { Locations } = useSelector((state) => state.location);
  const currentLocation = Locations.find((el) => el.id === params.id);

  const [pageLocation, setPageLocation] = useState({
    status: '',
    operationalStatus: '',
    name: '',
    description: '',
    mode: '',
    physicalType: '',
    partOf: '',
  });

  const [open, setOpen] = useState(false);
  const [valueSet, setValueSet] = useState(null);

  const handleOpen = (valueSet) => {
    setValueSet(valueSet);
    setOpen(true);
  };

  useEffect(() => {
    if (!currentLocation) return;
    setPageLocation(fLocation(currentLocation));
  }, [currentLocation]);

  /* A function that is called when the page loads. */
  const initializeData = useCallback(() => {
    fetchAndHandleData({
      dispatch,
      navigate,
      fetchAction: getLocation,
      id: params.id,
      errorMessage: 'No Location record found',
      navigateOnFailure: true,
      navigateTo: '/location/list',
    });
  }, [dispatch, navigate, params.id]);

  /* Calling the initializeData function when the component mounts. */
  useEffect(() => {
    initializeData();
  }, [initializeData]);

  return (
    <Page title="Location Information">
      <Container maxWidth="none">
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Stack direction="column" justifyContent="space-between">
            <Typography variant="h4" gutterBottom>
              Location Information
            </Typography>
            <Breadcrumbs aria-label="breadcrumb" mr={4}>
              <Link underline="hover" color="inherit" component={RouterLink} to="/location/list">
                Location
              </Link>
              <Typography color="text.primary">{pageLocation?.name || params.id}</Typography>
            </Breadcrumbs>
          </Stack>

          <Button
            variant="contained"
            component={RouterLink}
            to={`/location/edit/${params.id}`}
            startIcon={<EditIcon />}
          >
            Edit Location Information
          </Button>
        </Stack>

        <Grid container>
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Grid container item xs={12} columnSpacing={4} rowSpacing={3}>
                  <Grid item xs={12} md={12}>
                    <Stack spacing={3}>
                      <Stack spacing={2}>
                        <Typography variant="subtitle1" sx={{ pt: 1 }}>
                          Location Identification
                        </Typography>
                      </Stack>

                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <TextField
                          fullWidth
                          label="Location Name*"
                          value={pageLocation.name || ''}
                          style={{ border: 'none' }}
                          variant="standard"
                          InputProps={{
                            readOnly: true,
                          }}
                        />
                      </Stack>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <TextField
                          fullWidth
                          label="Description"
                          rows={2}
                          multiline
                          value={pageLocation.description || ''}
                          style={{ border: 'none' }}
                          variant="standard"
                          InputProps={{
                            readOnly: true,
                          }}
                        />
                      </Stack>
                      <Stack direction={{ xs: 'row' }} spacing={2}>
                        <TextField
                          fullWidth
                          select
                          label="Mode"
                          value={pageLocation.mode || ''}
                          style={{ border: 'none' }}
                          variant="standard"
                          InputProps={{
                            readOnly: true,
                          }}
                        >
                          {LOCATION_MODE.values.map((el) => (
                            <MenuItem key={el.code} value={el.code}>
                              {el.display}
                            </MenuItem>
                          ))}
                        </TextField>
                        <IconButton
                          aria-label="help"
                          onClick={() => {
                            handleOpen(LOCATION_MODE);
                          }}
                        >
                          <HelpOutlineIcon />
                        </IconButton>
                      </Stack>
                      <Stack direction={{ xs: 'row' }} spacing={2}>
                        <TextField
                          fullWidth
                          select
                          label="Physical Type"
                          value={pageLocation.physicalType || ''}
                          style={{ border: 'none' }}
                          variant="standard"
                          InputProps={{
                            readOnly: true,
                          }}
                        >
                          {LOCATION_PHYSICAL_TYPE.values.map((el) => (
                            <MenuItem key={el.code} value={el.code}>
                              {el.display}
                            </MenuItem>
                          ))}
                        </TextField>
                        <IconButton
                          aria-label="help"
                          onClick={() => {
                            handleOpen(LOCATION_PHYSICAL_TYPE);
                          }}
                        >
                          <HelpOutlineIcon />
                        </IconButton>
                      </Stack>
                      <Stack spacing={2}>
                        <Typography variant="subtitle1" sx={{ pt: 1 }}>
                          Location Status
                        </Typography>
                      </Stack>

                      <Stack direction={{ xs: 'row' }} spacing={2}>
                        <TextField
                          fullWidth
                          select
                          label="Status"
                          value={pageLocation.status || ''}
                          style={{ border: 'none' }}
                          variant="standard"
                          InputProps={{
                            readOnly: true,
                          }}
                        >
                          {LOCATION_STATUS.values.map((el) => (
                            <MenuItem key={el.code} value={el.code}>
                              {el.display}
                            </MenuItem>
                          ))}
                        </TextField>
                        <IconButton
                          aria-label="help"
                          onClick={() => {
                            handleOpen(LOCATION_STATUS);
                          }}
                        >
                          <HelpOutlineIcon />
                        </IconButton>
                      </Stack>

                      <Stack direction={{ xs: 'row' }} spacing={2}>
                        <TextField
                          fullWidth
                          select
                          label="Operational Status"
                          value={pageLocation.operationalStatus || ''}
                          style={{ border: 'none' }}
                          variant="standard"
                          InputProps={{
                            readOnly: true,
                          }}
                        >
                          {LOCATION_OPERATIONAL_STATUS.values.map((el) => (
                            <MenuItem key={el.code} value={el.code}>
                              {el.display}
                            </MenuItem>
                          ))}
                        </TextField>
                        <IconButton
                          aria-label="help"
                          onClick={() => {
                            handleOpen(LOCATION_OPERATIONAL_STATUS);
                          }}
                        >
                          <HelpOutlineIcon />
                        </IconButton>
                      </Stack>
                      <Stack spacing={2}>
                        <Typography variant="subtitle1" sx={{ pt: 1 }}>
                          Location Reference
                        </Typography>
                      </Stack>

                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <AutoCompleteLocation
                          fieldName="partOf"
                          values={pageLocation}
                          locationID={pageLocation?.partOf}
                          label="Part Of"
                          readOnly
                        />
                      </Stack>
                    </Stack>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        <FormHelpDialog isOpen={open} setIsOpen={setOpen} valueSet={valueSet} />
      </Container>
    </Page>
  );
}
